import type { FlairPart } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part, stack } from '../body';
import type { Worn } from '../headwear';
import { EYES, FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { fromPath, type HeadPicture, mask, type SetFlair } from './mythic';

/**
 * The Sheriff set (ADR-0160 amended, mythic): the law of a frontier town. A Stetson, a duster with a gold star on its
 * vest, and the Peacemaker.
 *
 * The head is the hat and what grows under it, over the wearer's own face (it shows through, in their skin): a tan felt
 * Stetson, its wide brim curling up at both sides (lit along its top, its underside in shade), its crown pinched and
 * creased down the middle; round the crown a band in the team's colour with a gold star concho on its front; under it
 * short brown hair at the back, a sideburn, and a thick horseshoe moustache drooping past the corners of the mouth.
 * Its flair: a glint on the concho, the hat's stampede string swinging under the chin.
 * The suit is a long tan duster over a dark leather vest: the team's bandana knotted at the throat, a big gold sheriff's
 * star on the vest's chest (the bright shape at 1:1, its points tipped with balls), a gun belt with a gold buckle and
 * cartridge loops; brown boots. Its flair: the star glinting, the duster's tails swinging, dust kicked up and spurs
 * striking sparks on the run.
 *
 * Rubric (`assetc mythic sheriff` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside the
 * Barnstormer): head S4 R4 P4 L4 C4 A4 F4, duster S4 R4 P4 L4 C4 F4. Weakest: the duster's tan on the lane's dirt; the
 * vest, the star and the bandana carry it there.
 */

// --- The Stetson and the lawman under it -----------------------------------------------------------------------------

const BRIM_U = -1;
const BRIM_W = 26;
/** The brim's top and its underside at u: flat over the brow, both curling up toward its sides. */
const brimTop = (u: number) => -9.5 - 6 * (Math.abs(u - BRIM_U) / BRIM_W) ** 3;
const brimBottom = (u: number) => -6 - 7.5 * (Math.abs(u - BRIM_U) / BRIM_W) ** 3;
/** The crown's sides (narrowing to its top) and its top: dented down the middle (the cattleman's crease). */
const CROWN = [-13, 12] as const;
const crownTop = (u: number) => -22 + 2.6 * Math.exp(-(((u + 0.5) / 4) ** 2)) + 1.6 * ((u + 0.5) / 12.5) ** 4;
/** The band round the crown's foot. */
const BAND = [-13.5, -10] as const;
/** The star concho on the band's front. */
const STAR = [6, -11.8] as const;
/** The horseshoe moustache: over the lip and down past the mouth's corners. */
const MOUSTACHE = [1.5, 14.2, 1.8, 11.8, 4, 10.6, 7.5, 10.3, 11, 10.6, 13, 11.8, 13.3, 14.2] as const;

/** Is (x, y) in a five-point star of radius r round (cx, cy), a point up. */
export function inStar(x: number, y: number, cx: number, cy: number, r: number): boolean {
  const [dx, dy] = [x - cx, y - cy];
  const a = Math.atan2(dx, -dy);
  const k = ((((a / (Math.PI * 2)) * 5) % 1) + 1) % 1;
  // Between a point (r) and a notch (0.45 r), straight edges.
  const edge = r * (0.45 + 0.55 * Math.abs(1 - 2 * k) ** 1.4);
  return Math.hypot(dx, dy) < edge;
}

/** Tan felt at light `l`. */
const felt = (l: number): PaletteKey => (l > 0.7 ? 'e' : l > 0.4 ? 't' : l > 0.05 ? 'm' : 'n');

const stetson: HeadPicture = (u, v) => {
  const d = Math.abs(u - BRIM_U);
  // The brim: lit along its top edge, its underside dark where it curls up.
  if (d <= BRIM_W && v >= brimTop(u) && v <= brimBottom(u)) {
    const into = v - brimTop(u);
    if (into < 1) return u < BRIM_U + 8 ? 'e' : 't';
    if (brimBottom(u) - v < 1.2) return 'N';
    return d > BRIM_W - 6 ? 'n' : u > 12 ? 'm' : 't';
  }
  // The crown over the brim, narrowing to its creased top.
  const narrow = Math.max(0, (BAND[0] - v) * 0.12);
  if (u >= CROWN[0] + narrow && u <= CROWN[1] - narrow && v >= crownTop(u) && v < brimTop(u) + 0.5) {
    // The band in the team's colour, a gold star on its front.
    if (v >= BAND[0] && v < BAND[1]) {
      if (inStar(u, v, STAR[0], STAR[1], 3.2)) return u < STAR[0] && v < STAR[1] ? 'y' : 'Y';
      return v < BAND[0] + 1 ? 'c' : v > BAND[1] - 1 ? 'B' : u > 8 ? 'B' : 'b';
    }
    // The crease down its middle, a pinch either side of the front.
    if (Math.abs(u + 0.5) < 0.8 && v < crownTop(u) + 4.5) return 'n';
    if (v - crownTop(u) < 1) return u < 0 ? 'e' : 't';
    const across = (u - CROWN[0]) / (CROWN[1] - CROWN[0]);
    if (Math.abs(across - 0.68) < 0.04 && v < BAND[0] - 1.5) return 'm';
    return felt(0.95 - across * 1.05);
  }
  if (v < brimBottom(u)) return null;
  // The horseshoe moustache: thick, brown, its top lit.
  const m = fromPath(u, v, MOUSTACHE);
  if (m < 1.1 && u > 0.3 && u < 14.5) return v < 10.6 ? 'm' : v > 12.5 ? 'N' : 'n';
  // Short brown hair at the back under the brim, a sideburn down to the jaw.
  if (u < -14 && u > -20 && v < 4 && Math.hypot(u + 2, v - 2) < 19.5) return v < -3 || u < -18.5 ? 'N' : 'n';
  if (u > -11 && u < -9 && v < 8) return 'n';
  return null;
};

/** The mask, pictured alone too (the wardrobe's tile): over the default face, its eyes showing. */
const STETSON = (): Worn => {
  const worn = mask(stetson);
  return { ...worn, alone: stack(FACE, EYES.sleepy.open, worn.front) };
};

// --- The duster ------------------------------------------------------------------------------------------------------

/** The duster standing (the frame's x 5-56, y 39-57): tan, a dark vest, the team's bandana, a gold star, a gun belt. */
function duster(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 - Math.max(0, y - 52) * 0.6 && x <= 43 + Math.max(0, y - 52) * 0.6) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  const arm = (x: number) => x < 16 || x > 43;
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const shade = !inSuit(x + 1, y) || x > 41;
      // Folds down the sleeves and the skirts.
      const fold = (x === 10 || x === 14 || x === 47 || x === 21 || x === 38) && y > 46;
      at(x, y, lit ? 't' : shade ? 'n' : fold ? 'n' : arm(x) && y > 51 ? 'n' : 'm');
    }
  // The vest in the open front: dark leather, its edges lit.
  for (let y = 43; y <= 52; y++)
    for (let x = 22; x <= 38; x++) at(x, y, x === 22 ? '3' : x > 35 ? '1' : y === 43 ? '3' : '2');
  // A shirt's V at the throat.
  for (let y = 43; y <= 46; y++)
    for (let x = 28; x <= 32; x++) if (Math.abs(x + 0.5 - 30.5) < 3 - (y - 43) * 0.7) at(x, y, x > 30 ? '5' : 'w');
  // The bandana in the team's colour, knotted at the throat, its point hanging.
  for (let x = 18; x <= 41; x++) {
    at(x, 41, x < 22 ? 'c' : 'b');
    at(x, 42, x > 38 ? 'B' : 'b');
  }
  c.stamp(
    `
    cbbbbbbbbB
    .cbbbbbbB.
    ..cbbbbB..
    ...cbbB...
    ....bB....
  `,
    25 - x0,
    43 - y0,
  );
  // The sheriff's star on the vest's near chest: gold, its points tipped with balls.
  for (let y = 44; y <= 52; y++)
    for (let x = 31; x <= 39; x++) {
      if (!inStar(x + 0.5, y + 0.5, 35.5, 48.3, 4.6)) continue;
      at(x, y, x + y < 82 ? 'y' : x + y > 85 ? 'U' : 'Y');
    }
  // The gun belt: dark leather, cartridge loops, a gold buckle.
  for (let x = 15; x <= 44; x++) {
    at(x, 53, 'N');
    at(x, 54, x % 2 ? 'N' : 'Y');
    at(x, 55, x > 41 ? 'N' : 'n');
  }
  c.stamp(
    `
    yYYYU
    Y...U
    UUUUU
  `,
    28 - x0,
    53 - y0,
  );
  return [c, x0, y0];
}

/** The duster lying (right of the legs, under the head): the belt (left), the vest and its star, the bandana. */
const DEAD_DUSTER = grid(`
  ....tttt
  ...tmmmm
  NYm222bc
  NNm222bb
  NYm222bb
  NNm2yY2b
  NYmyyYUb
  NNm2YU2b
  NYm2Y2Ub
  NNm222bb
  NYm222wb
  NNm222wB
  NYm2222b
  NNm2222b
  NYmmmmmB
  NNnnnnnn
  Nnnnnnnn
  ....nnnn
`);

/** Brown boots. */
const DUSTER = (): Outfit => ({ torso: duster(), dead: [DEAD_DUSTER, 24, 51], legs: { n: 'n', N: 'N', m: 'm' } });

// --- The Peacemaker's blued steel and gold on every other gun --------------------------------------------------------

/** Blued steel with gold engraving (the default gun is its own, `pollinator_sheriff`). */
const BLUED = { '3': '2', '4': '3', '5': 'Y' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const YELLOW = 0xfee761;
const GOLD = 0xfeae34;
const TAN = 0xe4a672;
const CREAM = 0xead4aa;
const BROWN = 0x733e39;
const MID = 0xb86f50;
const DARK = 0x3e2731;
const SILVER = 0xc0cbdc;
const GREY = 0x8b9bb4;

/** Dust kicked up off the boots on the run. */
const dust: FlairPart = {
  kind: 'emit',
  at: { on: 'feet', x: 30, y: 70 },
  box: [18, 2],
  rate: 0,
  run: 0.06,
  v: [-12, 4, -8, -2],
  g: 4,
  drag: 0.5,
  life: [0.45, 0.8],
  size: [2, 5],
  tones: [TAN, CREAM, MID],
  alpha: [0.7, 0],
  layer: 'back',
};

const FLAIR: SetFlair = {
  head: [
    // The stampede string under the chin, swinging.
    {
      kind: 'chain',
      at: { on: 'head', x: 21, y: 22 },
      n: 7,
      seg: 1.8,
      dir: Math.PI * 0.42,
      curl: -0.12,
      stiff: 5,
      g: 300,
      drag: 0.12,
      sway: [0.15, 0.6, 0],
      w: [1, 1, 2],
      shape: 'dots',
      tones: [BROWN, BROWN, BROWN, BROWN, BROWN, GOLD],
      rim: DARK,
      layer: 'front',
    },
    // A glint on the concho.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 36, y: 16 },
      box: [3, 3],
      n: 1,
      period: [0.8, 1.3],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  outfit: [
    // The duster's tails, swinging behind.
    {
      kind: 'chain',
      at: { on: 'torso', x: 15, y: 53 },
      n: 5,
      seg: 2,
      dir: Math.PI * 0.64,
      stiff: 5,
      g: 260,
      drag: 0.08,
      sway: [0.1, 0.6, 0],
      wind: 300,
      w: [7, 7, 6],
      shape: 'bars',
      tones: [MID, MID, BROWN],
      rim: DARK,
      lit: TAN,
      tatter: 2,
      layer: 'back',
    },
    // The star glinting.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 35.5, y: 48 },
      box: [7, 7],
      n: 1,
      period: [0.6, 1],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'torso', x: 35.5, y: 48 },
      r: [3, 4.5],
      alpha: [0.15, 0.3],
      period: 1.6,
      tone: GOLD,
      layer: 'front',
    },
    dust,
    // Spurs striking sparks on the run.
    {
      kind: 'emit',
      at: { on: 'feet', x: 20, y: 69 },
      box: [6, 2],
      rate: 0,
      run: 0.025,
      v: [-24, -8, -22, -8],
      g: 120,
      drag: 0.3,
      life: [0.15, 0.3],
      size: [1, 1],
      tones: [WHITE, YELLOW, GOLD],
      add: true,
      layer: 'front',
    },
  ],
  gun: [
    // A wisp of smoke curling off the muzzle now and then, a glint along the barrel.
    {
      kind: 'emit',
      at: { on: 'gun', x: 62, y: 18 },
      box: [1, 1],
      rate: 1.6,
      v: [-3, 3, -10, -6],
      g: -6,
      drag: 0.6,
      wobble: [1.5, 2],
      life: [0.5, 0.9],
      size: [1, 3],
      tones: [SILVER, GREY],
      alpha: [0.6, 0],
      layer: 'gun',
    },
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 45, y: 18 },
      box: [28, 2],
      n: 1,
      period: [0.6, 1.1],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Sheriff set (ADR-0160 amended). */
export const SHERIFF = drawnLater({ head: STETSON, outfit: DUSTER, gun: BLUED, flair: FLAIR });
